/// <reference path="../../types/youtube.d.ts" />

import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Box, IconButton, Slider, Typography, CircularProgress } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import useDevice from 'hooks/useDevice';

interface VideoPlayerProps {
    src: string;
    poster?: string;
    autoPlay?: boolean;
    loop?: boolean;
    muted?: boolean;
    controls?: boolean;
    isReel?: boolean;
    aspectRatio?: string;
    pointerEvents?: 'auto' | 'none' | 'unset' | string;
    loadingImage?: string;
    sx?: any
}

// Helper function to detect and extract YouTube video ID
const getYouTubeVideoId = (url: string): string | null => {
    const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
        /youtube\.com\/shorts\/([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
            return match[1];
        }
    }
    return null;
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({
    src,
    poster,
    autoPlay = false,
    loop = false,
    muted = false,
    controls = false,
    isReel = false,
    aspectRatio,
    pointerEvents = 'unset',
    loadingImage,
    sx
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const { isMobile } = useDevice()
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const [isMuted, setIsMuted] = useState(muted);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const hideControlsTimeout = useRef<NodeJS.Timeout | null>(null);

    // Detect if the source is a YouTube URL
    const youtubeVideoId = useMemo(() => getYouTubeVideoId(src), [src]);
    const isYouTube = !!youtubeVideoId;

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleTimeUpdate = () => setCurrentTime(video.currentTime);
        const handleLoadedMetadata = () => setDuration(video.duration);
        const handleEnded = () => setIsPlaying(false);

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('ended', handleEnded);

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('ended', handleEnded);
        };
    }, []);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const togglePlay = () => {
        const video = videoRef.current;
        if (!video) return;

        if (isPlaying) {
            video.pause();
        } else {
            video.play();
        }
        setIsPlaying(!isPlaying);
    };

    const toggleMute = () => {
        const video = videoRef.current;
        if (!video) return;

        video.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
        const video = videoRef.current;
        if (!video) return;

        const volumeValue = Array.isArray(newValue) ? newValue[0] : newValue;
        video.volume = volumeValue;
        setVolume(volumeValue);
        setIsMuted(volumeValue === 0);
    };

    const handleSeek = (_event: Event, newValue: number | number[]) => {
        const video = videoRef.current;
        if (!video) return;

        const seekTime = Array.isArray(newValue) ? newValue[0] : newValue;
        video.currentTime = seekTime;
        setCurrentTime(seekTime);
    };

    const toggleFullscreen = async () => {
        const container = containerRef.current;
        if (!container) return;

        try {
            if (!isFullscreen) {
                await container.requestFullscreen();
            } else {
                await document.exitFullscreen();
            }
        } catch (error) {
            console.error('Fullscreen error:', error);
        }
    };

    const formatTime = (time: number) => {
        if (isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleMouseMove = () => {
        setShowControls(true);
        if (hideControlsTimeout.current) {
            clearTimeout(hideControlsTimeout.current);
        }
        hideControlsTimeout.current = setTimeout(() => {
            if (isPlaying) {
                setShowControls(false);
            }
        }, 3000);
    };

    const handleMouseLeave = () => {
        if (isPlaying) {
            setShowControls(false);
        }
    };

    // YouTube iframe ref for API control
    const youtubeIframeRef = useRef<HTMLIFrameElement>(null);
    const [youtubeReady, setYoutubeReady] = useState(false);
    const [youtubePlayer, setYoutubePlayer] = useState<YTPlayer | null>(null);
    const [youtubeHasPlayed, setYoutubeHasPlayed] = useState(false);
    const [hideYoutubeOverlay, setHideYoutubeOverlay] = useState(false);

    useEffect(() => {
        if (!isYouTube) return;
        let timer: NodeJS.Timeout;

        if (youtubeHasPlayed) {
            // Once actual playback starts, wait 2.5s for YouTube's title/logo to fade
            timer = setTimeout(() => {
                setHideYoutubeOverlay(true);
            }, 3000);
        } else {
            // Fallback: if it never auto-plays, hide overlay after 6 seconds so it's not stuck
            timer = setTimeout(() => {
                setHideYoutubeOverlay(true);
            }, 6000);
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [youtubeHasPlayed, isYouTube]);

    // Build YouTube embed URL with parameters (hiding controls)
    const youtubeEmbedUrl = useMemo(() => {
        if (!youtubeVideoId) return '';
        const params = new URLSearchParams({
            autoplay: autoPlay ? '1' : '0',
            loop: loop ? '1' : '0',
            mute: muted ? '1' : '0',
            controls: controls ? '1' : '0', // Hide YouTube controls, we use custom or no controls
            modestbranding: '1',
            rel: '0',
            enablejsapi: '1', // Enable JavaScript API
            ...(loop && { playlist: youtubeVideoId }), // Required for loop to work
        });
        return `https://www.youtube.com/embed/${youtubeVideoId}?${params.toString()}`;
    }, [youtubeVideoId, autoPlay, loop, muted, controls]);

    // Load YouTube iframe API
    useEffect(() => {
        if (!isYouTube) return;

        // Load YouTube iframe API script
        if (!window.YT) {
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
        }

        // Initialize player when API is ready
        const initPlayer = () => {
            if (window.YT && window.YT.Player && youtubeIframeRef.current) {
                const player = new window.YT.Player(youtubeIframeRef.current, {
                    events: {
                        onReady: () => {
                            setYoutubeReady(true);
                            setYoutubePlayer(player);
                            // Explicitly mute and play if autoPlay is requested (helps browser autoplay policies)
                            if (autoPlay) {
                                if (muted) player.mute();
                                player.playVideo();
                            }
                        },
                        onStateChange: (event: YTOnStateChangeEvent) => {
                            // Update playing state based on YouTube player state
                            const YT = window.YT;
                            if (!YT) return;

                            if (event.data === YT.PlayerState.PLAYING) {
                                setIsPlaying(true);
                                setYoutubeHasPlayed(true);
                            } else if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
                                setIsPlaying(false);
                            }
                        },
                    },
                });
            }
        };

        if (window.YT && window.YT.Player) {
            initPlayer();
        } else {
            window.onYouTubeIframeAPIReady = initPlayer;
        }
    }, [isYouTube, youtubeVideoId]);

    // Toggle YouTube video play/pause
    const toggleYouTubePlay = () => {
        if (!youtubePlayer) return;

        if (isPlaying) {
            youtubePlayer.pauseVideo();
        } else {
            youtubePlayer.playVideo();
        }
    };

    // If it's a YouTube video, render iframe with custom controls
    if (isYouTube) {
        return (
            <Box
                ref={containerRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                sx={{
                    position: 'relative',
                    width: isReel ? '298px' : '100%',
                    height: isReel ? '530px' : 'auto',
                    margin: isReel ? '0 auto' : 0,
                    backgroundColor: '#000',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    '&:hover': {
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    },
                    transition: 'box-shadow 0.3s ease',
                    aspectRatio: isReel ? undefined : (aspectRatio || undefined),
                    paddingTop: isReel ? 0 : (aspectRatio ? undefined : '56.25%'), // Use aspectRatio if provided, else fallback to 16:9
                    ...sx
                }}
            >
                <iframe
                    ref={youtubeIframeRef}
                    src={youtubeEmbedUrl}
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{
                        position: isReel ? 'static' : 'absolute',
                        top: isReel ? 'auto' : 0,
                        left: isReel ? 'auto' : 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        pointerEvents: pointerEvents as any,
                    }}
                />

                {/* Loading overlay to cover YouTube logo and title at the beginning */}
                {!hideYoutubeOverlay && pointerEvents === 'none' && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: isMobile ? '-8px' : '-16.5px',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: '#000',
                            backgroundImage: loadingImage ? `url(${loadingImage})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            zIndex: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            transition: 'opacity 0.5s ease-out',
                        }}
                    >
                        {!loadingImage && <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.7)' }} size={40} />}
                    </Box>
                )}

                {/* Custom Play Button Overlay for YouTube */}
                {controls && (
                    <Box
                        onClick={toggleYouTubePlay}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            cursor: 'pointer',
                            opacity: showControls ? 1 : 0,
                            transition: 'opacity 0.3s ease',
                            zIndex: 10,
                            pointerEvents: showControls ? 'auto' : 'none',
                        }}
                    >
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'transform 0.2s ease, backgroundColor 0.2s ease',
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                },
                            }}
                        >
                            {isPlaying ? (
                                <PauseIcon sx={{ fontSize: 48, color: '#000' }} />
                            ) : (
                                <PlayArrowIcon sx={{ fontSize: 48, color: '#000', ml: 0.5 }} />
                            )}
                        </Box>
                    </Box>
                )}

                {/* Click overlay to pause when playing */}
                {isPlaying && controls && (
                    <Box
                        onClick={toggleYouTubePlay}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            cursor: 'pointer',
                            zIndex: 5,
                        }}
                    />
                )}
            </Box>
        );
    }

    // Otherwise, render native video player with custom controls
    return (
        <Box
            ref={containerRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            sx={{
                position: 'relative',
                width: isReel ? '298px' : '100%',
                height: isReel ? '550px' : 'auto',
                margin: isReel ? '0 auto' : 0,
                backgroundColor: '#000',
                // borderRadius: '12px',
                overflow: 'hidden',
                // boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                // '&:hover': {
                // boxShadow: '0 12px 48px rgba(0, 0, 0, 0.4)',
                // },
                transition: 'box-shadow 0.3s ease',
                ...sx
            }}
        >
            <video
                ref={videoRef}
                src={src}
                poster={poster}
                autoPlay={autoPlay}
                loop={loop}
                muted={muted}
                playsInline={true}
                onClick={controls ? togglePlay : undefined}
                style={{
                    width: '100%',
                    height: isReel ? '100%' : 'auto',
                    objectFit: isReel ? 'cover' : 'contain',
                    display: 'block',
                    cursor: controls ? 'pointer' : 'default',
                }}
            />

            {/* Controls Overlay */}
            {controls && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
                        padding: '20px',
                        opacity: showControls ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: showControls ? 'auto' : 'none',
                    }}
                >
                    {/* Progress Bar */}
                    <Slider
                        value={currentTime}
                        max={duration || 100}
                        onChange={handleSeek}
                        sx={{
                            color: '#fff',
                            height: 4,
                            mb: 0,
                            '& .MuiSlider-thumb': {
                                width: 12,
                                height: 12,
                                transition: 'width 0.2s, height 0.2s',
                                '&:hover, &.Mui-focusVisible': {
                                    width: 16,
                                    height: 16,
                                },
                            },
                            '& .MuiSlider-rail': {
                                opacity: 0.3,
                            },
                        }}
                    />

                    {/* Control Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                            <IconButton
                                onClick={togglePlay}
                                sx={{
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    },
                                }}
                            >
                                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>

                            <IconButton
                                onClick={toggleMute}
                                sx={{
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    },
                                }}
                            >
                                {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                            </IconButton>

                            <Slider
                                value={isMuted ? 0 : volume}
                                max={1}
                                step={0.01}
                                onChange={handleVolumeChange}
                                sx={{
                                    width: 80,
                                    color: '#fff',
                                    ml: 1,
                                    '& .MuiSlider-thumb': {
                                        width: 10,
                                        height: 10,
                                    },
                                }}
                            />

                            <Typography
                                sx={{
                                    color: '#fff',
                                    fontSize: '14px',
                                    ml: 2,
                                    fontFamily: 'monospace',
                                }}
                            >
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={toggleFullscreen}
                            sx={{
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                            }}
                        >
                            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                    </Box>
                </Box>
            )}

            {/* Play Button Overlay (when paused) */}
            {controls && (
                <Box
                    onClick={togglePlay}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        cursor: 'pointer',
                        opacity: showControls ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: showControls ? 'auto' : 'none',
                    }}
                >
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'transform 0.2s ease, backgroundColor 0.2s ease',
                            '&:hover': {
                                transform: 'scale(1.1)',
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                            },
                        }}
                    >
                        {isPlaying ? (
                            <PauseIcon sx={{ fontSize: 48, color: '#000' }} />
                        ) : (
                            <PlayArrowIcon sx={{ fontSize: 48, color: '#000', ml: 0.5 }} />
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default VideoPlayer;
